/**
 * Settings: reached from the icon in the header, never a section of its own.
 *
 * One thing for now — the person's own password. The current one is asked
 * again, and every other session of theirs ends with the change; this one
 * stays, so they are not signed out of the screen they are using.
 */
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/design-system/Button";
import { PageHeader } from "@/design-system/PageHeader";
import { useLegalDocuments } from "@/shared/legal/legal";
import { Panel } from "@/design-system/Panel";
import { changePassword } from "@/shared/api";

const MINIMUM = 8;
const FIELD =
  "min-h-9 w-full rounded-row border border-border-strong bg-background-base px-2 text-text-primary focus-visible:outline-2 focus-visible:outline-focus-ring";

export function SettingsPage(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2" data-testid="settings-page">
      <PageHeader title="Настройки" description="Ваш вход в Skryptos." />
      <div className="w-full max-w-xl">
        <PasswordPanel />
      </div>
      <LegalDocuments />
    </div>
  );
}

/** The person's own password; every other session ends with the change. */
export function PasswordPanel(): React.JSX.Element {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [done, setDone] = useState(false);
  const change = useMutation({
    mutationFn: async () => {
      const { response } = await changePassword({ body: { current_password: current, new_password: next } });
      if (response?.status === 403) throw new Error("current");
      if (response?.ok !== true) throw new Error("refused");
    },
    onSuccess: () => { setDone(true); },
    onSettled: () => { setCurrent(""); setNext(""); setRepeat(""); },
  });
  const tooShort = next !== "" && next.length < MINIMUM;
  const mismatch = repeat !== "" && repeat !== next;
  const ready = current !== "" && next.length >= MINIMUM && repeat === next && !change.isPending;

  return (
        <Panel title="Пароль" testId="password-panel">
          <form className="flex flex-col gap-3 text-body-main text-text-secondary"
            onSubmit={(event) => { event.preventDefault(); if (ready) { setDone(false); change.mutate(); } }}>
            <label className="flex flex-col gap-1">
              <span>Текущий пароль</span>
              <input aria-label="Текущий пароль" className={FIELD} type="password" autoComplete="current-password" required value={current} onChange={(event) => { setCurrent(event.target.value); }} />
            </label>
            <label className="flex flex-col gap-1">
              <span>Новый пароль, не короче {MINIMUM} символов</span>
              <input aria-label="Новый пароль" className={FIELD} type="password" autoComplete="new-password" required value={next} onChange={(event) => { setNext(event.target.value); }} />
            </label>
            <label className="flex flex-col gap-1">
              <span>Новый пароль ещё раз</span>
              <input aria-label="Новый пароль ещё раз" className={FIELD} type="password" autoComplete="new-password" required value={repeat} onChange={(event) => { setRepeat(event.target.value); }} />
            </label>
            {tooShort && <p className="text-helper-sm text-status-warning">Нужно не меньше {MINIMUM} символов.</p>}
            {mismatch && <p className="text-helper-sm text-status-warning">Пароли не совпадают.</p>}
            <div><Button type="submit" variant="primary" disabled={!ready}>Сменить пароль</Button></div>
            {done && <p role="status" className="text-status-positive">Пароль изменён. Остальные входы завершены, этот остаётся.</p>}
            {change.isError && (
              <p role="alert" className="text-status-warning">
                {change.error.message === "current" ? "Текущий пароль не подошёл." : "Пароль не изменён. Попробуйте ещё раз."}
              </p>
            )}
          </form>
        </Panel>
  );
}

/** The documents the client accepted, readable at any time; nothing on the platform. */
export function LegalDocuments(): React.JSX.Element | null {
  const query = useLegalDocuments();
  if (query.data === undefined) return null;
  return (
    <div className="w-full max-w-3xl">
      <Panel title="Документы" testId="legal-documents">
        <div className="flex flex-col gap-2">
          {query.data.documents.map((document) => (
            <details key={document.kind} className="rounded-md border border-border-subtle">
              <summary className="cursor-pointer px-3 py-2 text-body-main text-text-primary transition-colors duration-150 hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-focus-ring">
                {document.title} · редакция от {document.version}{document.accepted ? " · принято" : ""}
              </summary>
              <div className="whitespace-pre-line px-3 pb-3 text-body-main text-text-secondary">{document.body}</div>
            </details>
          ))}
        </div>
      </Panel>
    </div>
  );
}
